import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import TitlesSkeleton from './TitlesSkeleton';
import ErrorState from '../ErrorState/ErrorState';
import { useCachedFetch } from './../../hooks/useCachedFetch';
import { AnimatedOnScroll } from '../AnimatedOnScroll';
import './Titles.scss';

const Titles = ({
    page,
    pageType,
    title,
    subtitle,
    contactLink,
    contactText,
    additionalElementClassName = 'titles__wrapper__additional',
}) => {
    const { i18n } = useTranslation();
    const currentLang = i18n.language;
    
    const { data: dynamicData, loading: dynamicLoading, error: dynamicError, refetch: refetchDynamic } = useCachedFetch('/data/locales/dynamic.json');
    const { data: mainData, loading: mainLoading, error: mainError, refetch: refetchMain } = useCachedFetch('/data/locales/mainData.json');
    const { data: sitemapData, loading: sitemapLoading, error: sitemapError, refetch: refetchSitemap } = useCachedFetch('/data/locales/sitemap.json');
    const { data: legalData, loading: legalLoading, error: legalError, refetch: refetchLegal } = useCachedFetch('/data/locales/legal.json');

    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const [pageData, setPageData] = useState(null);

    const getLocalizedText = useCallback((textObj) => {
        if (!textObj) return '';
        if (typeof textObj === 'string') return textObj;
        if (textObj && typeof textObj === 'object') {
            return textObj[currentLang] || textObj.en || '';
        }
        return '';
    }, [currentLang]);

    const getSkeletonConfig = useMemo(() => {
        if (page === 'blogPage' || pageType === 'sitemap') {
            return {
                hideSubtitle: true,
                hidePrimarySubtitle: true
            };
        }
        
        return {
            hideSubtitle: false,
            hidePrimarySubtitle: false
        };
    }, [page, pageType]);

    const handleRetry = useCallback(() => {
        if (pageType === 'sitemap') {
            refetchSitemap();
        } else if (pageType === 'privacy' || pageType === 'terms' || pageType === 'impressum') {
            refetchLegal();
        } else if (page) {
            refetchDynamic();
            refetchMain();
        }
    }, [page, pageType, refetchDynamic, refetchMain, refetchSitemap, refetchLegal]);

    useEffect(() => {
        if (title !== undefined || subtitle !== undefined) {
            setPageData({
                contentTitle: title,
                contentSubtitle: subtitle
            });
            setIsLoading(false);
            setHasError(false);
            return;
        }

        if (pageType === 'sitemap') {
            if (sitemapLoading) return;
            if (sitemapError) {
                setHasError(true);
                setIsLoading(false);
                return;
            }
        } else if (pageType === 'privacy' || pageType === 'terms') {
            if (legalLoading) return;
            if (legalError) {
                setHasError(true);
                setIsLoading(false);
                return;
            }
        } else if (page) {
            if (dynamicLoading && mainLoading) return;
            if (dynamicError && mainError) {
                setHasError(true);
                setIsLoading(false);
                return;
            }
        }

        try {
            setHasError(false);

            if (pageType === 'sitemap' || pageType === 'privacy' || pageType === 'terms' || pageType === 'impressum') {
                let data;
                
                if (pageType === 'sitemap') {
                    const langData = sitemapData?.translations?.[currentLang] || sitemapData?.translations?.en;
                    if (langData) {
                        data = {
                            sitemapTitle: langData.sitemapTitle,
                            sitemapSubtitle: '',
                            sitemapUpdatedAt: ''
                        };
                    }
                } else {
                    data = legalData?.pages?.[pageType];
                }
                
                if (data) {
                    setPageData(data);
                } else {
                    setHasError(true);
                }
            } 
            else if (page) {
                const pageDataFromDynamic = dynamicData?.[page];
                
                if (pageDataFromDynamic && pageDataFromDynamic.title && pageDataFromDynamic.subtitle) {
                    const processedData = {
                        contentTitle: getLocalizedText(pageDataFromDynamic.title),
                        contentSubtitle: getLocalizedText(pageDataFromDynamic.subtitle),
                        buttonText: getLocalizedText(pageDataFromDynamic.buttonText),
                        buttonLink: pageDataFromDynamic.buttonLink
                    };
                    
                    if (processedData.contentTitle && processedData.contentSubtitle) {
                        setPageData(processedData);
                        setIsLoading(false);
                        return;
                    }
                }

                const pageDataFromMain = mainData?.TopMainData?.[page];
                
                if (pageDataFromMain) {
                    setPageData(pageDataFromMain);
                } else {
                    setHasError(true);
                    console.warn(`Page "${page}" not found in dynamic.json, but may exist in mainData.json`);
                }
            } else {
                setHasError(true);
                console.error('No page or pageType provided');
            }
        } catch (error) {
            console.error('Error processing titles data:', error);
            setHasError(true);
        } finally {
            setIsLoading(false);
        }
    }, [
        page, pageType, title, subtitle, currentLang,
        dynamicData, dynamicLoading, dynamicError,
        mainData, mainLoading, mainError,
        sitemapData, sitemapLoading, sitemapError,
        legalData, legalLoading, legalError,
        getLocalizedText
    ]);

    const renderData = useMemo(() => {
        if (!pageData) return {};

        let titleText, subtitleText, updatedAt, buttonText, buttonLink;

        if (pageType === 'sitemap') {
            titleText = getLocalizedText(pageData.sitemapTitle);
            subtitleText = '';
            updatedAt = '';
        } else if (pageType === 'privacy' || pageType === 'terms' || pageType === 'impressum') {
            titleText = getLocalizedText(pageData[`${pageType}Title`]);
            subtitleText = getLocalizedText(pageData[`${pageType}Subtitle`]);
            updatedAt = pageData[`${pageType}UpdatedAt`];
        } else {
            titleText = getLocalizedText(pageData.contentTitle || pageData.faq__title);
            subtitleText = getLocalizedText(pageData.contentSubtitle || pageData.faq__subtitle);
            buttonText = pageData.buttonText;
            buttonLink = pageData.buttonLink;
            updatedAt = '';
        }
            
        const contactTextLocalized = getLocalizedText(contactText) || buttonText;

        return {
            titleText,
            subtitleText,
            updatedAt,
            buttonText,
            buttonLink,
            contactTextLocalized
        };
    }, [pageData, pageType, contactText, getLocalizedText]);

    const hasValidData = useMemo(() => 
        !hasError && pageData && renderData.titleText,
        [hasError, pageData, renderData.titleText]
    );

    if (isLoading) {
        return <TitlesSkeleton {...getSkeletonConfig} />;
    }

    if (!hasValidData) {
        return <ErrorState onRetry={handleRetry} />;
    }

    const { titleText, subtitleText, updatedAt, buttonLink, contactTextLocalized } = renderData;

    return (
        <div className='titles__section'>
            <div className='titles__wrapper'>
                <AnimatedOnScroll animation="fade-up" delay={0}>
                {titleText && (
                    <h2 className='titles__wrapper__title'>
                        {titleText}
                    </h2>
                )}
                </AnimatedOnScroll>
                <AnimatedOnScroll animation="fade-up" delay={100}>
                {(subtitleText || contactLink || buttonLink || updatedAt) && (
                    <p className='titles__wrapper__subtitle'>
                        {subtitleText}
                        {(contactLink || buttonLink) && contactTextLocalized && (
                            <> {' '}
                                <Link to={contactLink || buttonLink} className='titles__wrapper__link'>
                                    {contactTextLocalized}
                                </Link>
                            </>
                        )}
                        {updatedAt && (
                            <span className={additionalElementClassName}>
                                {updatedAt}
                            </span>
                        )}
                    </p>
                )}
                </AnimatedOnScroll>
            </div>
        </div>
    );
};

export default React.memo(Titles);